import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent,
    Button, Box, Typography, TextField, Alert, IconButton,
    CircularProgress, Chip, Paper, Grid
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import SecurityIcon from '@mui/icons-material/Security';
import { useThemeContext } from './contexts/ThemeContext';
import { useAuthStore } from '../store/authStore';
import { urlBase } from '../config';

export default function TwoFactorSetupDialog({ open, onClose, token, onSuccess }) {
    const { mode } = useThemeContext();
    const darkTheme = mode === 'dark';
    const { updateAccessToken, setTwoFactorEnabled } = useAuthStore();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [step, setStep] = useState('STATUS'); // 'STATUS' | 'QR' | 'BACKUP_CODES'
    const [statusData, setStatusData] = useState({ twoFactorEnabled: false, eligible: true });
    const [setupData, setSetupData] = useState({ secretKey: '', qrCodeDataUri: '' });
    const [setupPassword, setSetupPassword] = useState('');
    const [verifyCode, setVerifyCode] = useState('');
    const [backupCodes, setBackupCodes] = useState([]);
    const [copiedSecret, setCopiedSecret] = useState(false);
    const [copiedCodes, setCopiedCodes] = useState(false);

    const authHeaders = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
    };

    const fetchStatus = async () => {
        if (!token) return;
        setLoading(true);
        setError('');
        try {
            const resp = await fetch(`${urlBase}/api/auth/2fa/status`, {
                headers: authHeaders
            });
            if (resp.ok) {
                const data = await resp.json();
                setStatusData(data);
                setStep('STATUS');
            } else {
                setError('Nie udało się pobrać statusu 2FA');
            }
        } catch (err) {
            setError('Błąd połączenia z serwerem');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) {
            fetchStatus();
            setSetupPassword('');
            setVerifyCode('');
            setBackupCodes([]);
            setError('');
        }
    }, [open]);

    const handleStartSetup = async () => {
        if (!setupPassword) {
            setError('Wprowadź aktualne hasło do konta, aby rozpocząć konfigurację 2FA.');
            return;
        }
        setLoading(true);
        setError('');
        try {
            const resp = await fetch(`${urlBase}/api/auth/2fa/setup`, {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify({ password: setupPassword })
            });
            if (resp.ok) {
                const data = await resp.json();
                setSetupData(data);
                setSetupPassword('');
                setStep('QR');
                setVerifyCode('');
            } else {
                const errText = await resp.text();
                setError(errText || 'Nie udało się zainicjować konfiguracji 2FA');
            }
        } catch (err) {
            setError('Błąd połączenia z serwerem');
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmSetup = async () => {
        if (!verifyCode || verifyCode.trim().length !== 6) {
            setError('Wprowadź poprawny 6-cyfrowy kod weryfikacyjny z aplikacji');
            return;
        }
        setLoading(true);
        setError('');
        try {
            const resp = await fetch(`${urlBase}/api/auth/2fa/confirm`, {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify({ code: verifyCode.trim() })
            });
            if (resp.ok) {
                const data = await resp.json();
                if (data.accessToken) {
                    updateAccessToken(data.accessToken, data.refreshToken);
                }
                setTwoFactorEnabled(true);
                setBackupCodes(data.backupCodes || []);
                setStatusData(prev => ({ ...prev, twoFactorEnabled: true }));
                setStep('BACKUP_CODES');
                if (onSuccess) onSuccess('2FA zostało pomyślnie aktywowane!');
            } else {
                const errData = await resp.text();
                setError(errData || 'Niepoprawny kod weryfikacyjny. Spróbuj ponownie.');
            }
        } catch (err) {
            setError('Błąd połączenia z serwerem');
        } finally {
            setLoading(false);
        }
    };

    const handleCopySecret = () => {
        if (setupData.secretKey) {
            navigator.clipboard.writeText(setupData.secretKey);
            setCopiedSecret(true);
            setTimeout(() => setCopiedSecret(false), 2500);
        }
    };

    const handleCopyBackupCodes = () => {
        if (backupCodes.length > 0) {
            navigator.clipboard.writeText(backupCodes.join('\n'));
            setCopiedCodes(true);
            setTimeout(() => setCopiedCodes(false), 2500);
        }
    };

    const handleDownloadBackupCodes = () => {
        if (backupCodes.length === 0) return;
        const textContent = `PELIKAN - JEDNORAZOWE KODY ZAPASOWE 2FA\nData wygenerowania: ${new Date().toLocaleString('pl-PL')}\n\nKażdy kod może zostać użyty tylko jeden raz:\n` +
            backupCodes.map((code, idx) => `${idx + 1}. ${code}`).join('\n') +
            '\n\nPrzechowuj te kody w bezpiecznym miejscu (np. menedżerze haseł).';
        const blob = new Blob([textContent], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `pelikan-kody-zapasowe-2fa-${new Date().toISOString().slice(0, 10)}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '16px',
                    backgroundColor: darkTheme ? '#1E293B' : '#FFFFFF',
                    color: darkTheme ? '#F8FAFC' : '#0F172A',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 8px 10px -6px rgba(0, 0, 0, 0.2)'
                }
            }}
        >
            <DialogTitle sx={{ m: 0, p: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '10px',
                            background: 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#FFFFFF'
                        }}
                    >
                        <SecurityIcon fontSize="small" />
                    </Box>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '18px', lineHeight: 1.2 }}>
                            Weryfikacja dwuskładnikowa (2FA)
                        </Typography>
                        <Typography variant="caption" sx={{ color: darkTheme ? '#94A3B8' : '#64748B' }}>
                            Google Authenticator / TOTP
                        </Typography>
                    </Box>
                </Box>
                <IconButton onClick={onClose} size="small" sx={{ color: darkTheme ? '#94A3B8' : '#64748B' }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ borderColor: darkTheme ? 'rgba(255,255,255,0.08)' : '#E2E8F0', p: 3 }}>
                {error && (
                    <Alert severity="error" sx={{ mb: 2.5, borderRadius: '10px' }} onClose={() => setError('')}>
                        {error}
                    </Alert>
                )}

                {loading ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 5, gap: 2 }}>
                        <CircularProgress size={36} />
                        <Typography variant="body2" sx={{ color: darkTheme ? '#94A3B8' : '#64748B' }}>
                            Przetwarzanie…
                        </Typography>
                    </Box>
                ) : step === 'STATUS' ? (
                    /* WIDOK STATUSU 2FA */
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2.5,
                                borderRadius: '12px',
                                border: `1px solid ${statusData.twoFactorEnabled
                                    ? (darkTheme ? 'rgba(16, 185, 129, 0.3)' : '#A7F3D0')
                                    : (darkTheme ? 'rgba(255, 255, 255, 0.1)' : '#E2E8F0')}`,
                                backgroundColor: statusData.twoFactorEnabled
                                    ? (darkTheme ? 'rgba(16, 185, 129, 0.1)' : '#ECFDF5')
                                    : (darkTheme ? 'rgba(255, 255, 255, 0.03)' : '#F8FAFC')
                            }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                    Status ochrony konta
                                </Typography>
                                <Chip
                                    icon={statusData.twoFactorEnabled ? <CheckCircleIcon fontSize="small" /> : undefined}
                                    label={statusData.twoFactorEnabled ? '2FA Aktywne' : '2FA Wyłączone'}
                                    color={statusData.twoFactorEnabled ? 'success' : 'default'}
                                    size="small"
                                    sx={{ fontWeight: 600 }}
                                />
                            </Box>
                            <Typography variant="body2" sx={{ color: darkTheme ? '#CBD5E1' : '#475569', lineHeight: 1.6 }}>
                                {statusData.twoFactorEnabled
                                    ? 'Twoje konto jest zabezpieczone weryfikacją dwuskładnikową. Przy każdym logowaniu wymagany jest kod z aplikacji Google Authenticator.'
                                    : 'Dodaj drugą warstwę zabezpieczeń do swojego konta. Do zalogowania oprócz hasła wymagany będzie 6-cyfrowy kod z telefonu.'}
                            </Typography>
                        </Paper>

                        {!statusData.eligible ? (
                            <Alert severity="info" sx={{ borderRadius: '10px' }}>
                                Uwierzytelnianie dwuskładnikowe nie jest dostępne dla Twojej roli.
                            </Alert>
                        ) : statusData.twoFactorEnabled ? (
                            <Alert severity="info" sx={{ borderRadius: '10px' }}>
                                Ze względów bezpieczeństwa aktywnej weryfikacji 2FA nie można wyłączyć samodzielnie. W razie wymiany lub utraty telefonu skontaktuj się z administratorem systemu w celu zresetowania 2FA.
                            </Alert>
                        ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                <Typography variant="body2" sx={{ color: darkTheme ? '#CBD5E1' : '#475569' }}>
                                    Potwierdź swoje aktualne hasło, aby wygenerować klucz parowania 2FA:
                                </Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="password"
                                    placeholder="Wprowadź aktualne hasło do konta"
                                    value={setupPassword}
                                    onChange={(e) => setSetupPassword(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter' && setupPassword) handleStartSetup(); }}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                                />
                                <Button
                                    variant="contained"
                                    disabled={!setupPassword}
                                    onClick={handleStartSetup}
                                    sx={{
                                        py: 1.2,
                                        borderRadius: '10px',
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        background: 'linear-gradient(135deg, #1E40AF 0%, #3B82F6 100%)',
                                        boxShadow: '0 4px 14px rgba(59,130,246,0.3)'
                                    }}
                                >
                                    Włącz weryfikację dwuskładnikową
                                </Button>
                            </Box>
                        )}
                    </Box>
                ) : step === 'QR' ? (
                    /* WIDOK KROKU 1 I 2: SKANOWANIE I POTWIERDZENIE KODEM */
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        <Typography variant="body2" sx={{ color: darkTheme ? '#CBD5E1' : '#475569' }}>
                            <b>Krok 1:</b> Otwórz aplikację <b>Google Authenticator</b> (lub inną aplikację TOTP) w telefonie, kliknij <b>Dodaj (+)</b> i zeskanuj poniższy kod QR:
                        </Typography>

                        {/* Obrazek QR */}
                        <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
                            <Box
                                sx={{
                                    p: 1.5,
                                    backgroundColor: '#FFFFFF',
                                    borderRadius: '12px',
                                    border: '1px solid #E2E8F0',
                                    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
                                }}
                            >
                                {setupData.qrCodeDataUri && (
                                    <img
                                        src={setupData.qrCodeDataUri}
                                        alt="Kod QR do 2FA"
                                        style={{ width: 190, height: 190, display: 'block' }}
                                    />
                                )}
                            </Box>
                        </Box>

                        {/* Klucz tekstowy awaryjny */}
                        <Box>
                            <Typography variant="caption" sx={{ color: darkTheme ? '#94A3B8' : '#64748B', display: 'block', mb: 0.5 }}>
                                Nie możesz zeskanować? Wpisz klucz ręcznie:
                            </Typography>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 1.2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    borderRadius: '8px',
                                    backgroundColor: darkTheme ? 'rgba(255,255,255,0.05)' : '#F1F5F9',
                                    border: `1px solid ${darkTheme ? 'rgba(255,255,255,0.1)' : '#E2E8F0'}`
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontFamily: 'monospace',
                                        fontSize: '13px',
                                        fontWeight: 600,
                                        letterSpacing: '1px',
                                        wordBreak: 'break-all'
                                    }}
                                >
                                    {setupData.secretKey}
                                </Typography>
                                <IconButton size="small" onClick={handleCopySecret} title="Kopiuj klucz">
                                    {copiedSecret ? <CheckCircleIcon fontSize="small" color="success" /> : <ContentCopyIcon fontSize="small" />}
                                </IconButton>
                            </Paper>
                        </Box>

                        {/* Krok 2: Wpisanie 6 cyfr */}
                        <Box sx={{ mt: 1 }}>
                            <Typography variant="body2" sx={{ color: darkTheme ? '#CBD5E1' : '#475569', mb: 1 }}>
                                <b>Krok 2:</b> Wprowadź 6-cyfrowy kod wygenerowany przez aplikację, aby potwierdzić sparowanie:
                            </Typography>
                            <TextField
                                fullWidth
                                placeholder="000 000"
                                value={verifyCode}
                                onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                inputProps={{
                                    style: {
                                        textAlign: 'center',
                                        letterSpacing: '6px',
                                        fontSize: '22px',
                                        fontWeight: '700',
                                        fontFamily: 'monospace'
                                    }
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': { borderRadius: '10px' }
                                }}
                            />
                        </Box>

                        <Box sx={{ display: 'flex', gap: 1.5, mt: 1 }}>
                            <Button
                                variant="contained"
                                fullWidth
                                onClick={handleConfirmSetup}
                                disabled={verifyCode.length !== 6}
                                sx={{
                                    py: 1.2,
                                    borderRadius: '10px',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    background: 'linear-gradient(135deg, #1E40AF 0%, #3B82F6 100%)'
                                }}
                            >
                                Potwierdź i aktywuj 2FA
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={() => setStep('STATUS')}
                                sx={{ borderRadius: '10px', textTransform: 'none' }}
                            >
                                Anuluj
                            </Button>
                        </Box>
                    </Box>
                ) : (
                    /* WIDOK KROKU 3: KODY ZAPASOWE */
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        <Alert severity="success" sx={{ borderRadius: '10px' }}>
                            Uwierzytelnianie dwuskładnikowe zostało pomyślnie aktywowane!
                        </Alert>

                        <Typography variant="body2" sx={{ color: darkTheme ? '#CBD5E1' : '#475569', lineHeight: 1.6 }}>
                            <b>Zapisz swoje jednorazowe kody zapasowe.</b> Będziesz ich potrzebować, jeśli zgubisz telefon lub utracisz dostęp do aplikacji Google Authenticator.
                        </Typography>

                        {/* Siatka kodów zapasowych */}
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                borderRadius: '12px',
                                backgroundColor: darkTheme ? 'rgba(255,255,255,0.04)' : '#F8FAFC',
                                border: `1px solid ${darkTheme ? 'rgba(255,255,255,0.1)' : '#E2E8F0'}`
                            }}
                        >
                            <Grid container spacing={1.5}>
                                {backupCodes.map((code, idx) => (
                                    <Grid item xs={6} key={idx}>
                                        <Box
                                            sx={{
                                                p: 1,
                                                textAlign: 'center',
                                                fontFamily: 'monospace',
                                                fontSize: '14px',
                                                fontWeight: 700,
                                                letterSpacing: '1px',
                                                borderRadius: '6px',
                                                backgroundColor: darkTheme ? 'rgba(0,0,0,0.2)' : '#FFFFFF',
                                                border: `1px solid ${darkTheme ? 'rgba(255,255,255,0.08)' : '#E2E8F0'}`
                                            }}
                                        >
                                            {code}
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        </Paper>

                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                            <Button
                                variant="outlined"
                                fullWidth
                                startIcon={copiedCodes ? <CheckCircleIcon color="success" /> : <ContentCopyIcon />}
                                onClick={handleCopyBackupCodes}
                                sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}
                            >
                                {copiedCodes ? 'Skopiowano kody' : 'Kopiuj wszystkie'}
                            </Button>
                            <Button
                                variant="outlined"
                                fullWidth
                                startIcon={<DownloadIcon />}
                                onClick={handleDownloadBackupCodes}
                                sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}
                            >
                                Pobierz jako .txt
                            </Button>
                        </Box>

                        <Button
                            variant="contained"
                            fullWidth
                            onClick={() => {
                                setStep('STATUS');
                                onClose();
                            }}
                            sx={{
                                py: 1.2,
                                mt: 1,
                                borderRadius: '10px',
                                fontWeight: 600,
                                textTransform: 'none',
                                background: 'linear-gradient(135deg, #1E40AF 0%, #3B82F6 100%)'
                            }}
                        >
                            Zakończ konfigurację
                        </Button>
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
}
